import mergeRefs from 'merge-refs';
import * as React from 'react';
import { useRef } from 'react';
import { cn } from 'v5/platform/dom/cn';
import { hideNumberInputSpinnersClassName } from 'v5/platform/dom/hideNumberInputSpinnersClassName';
import {
  input2ClassName,
  textArea2ClassName,
  type Input2ChromeProps,
  type Input2Size,
  type Input2Tone,
  type Input2Variant
} from './_common/input2Chrome';

export type Input2Props = Omit<
  React.ComponentProps<'input'>,
  'size' | 'readOnly'
> &
  Input2ChromeProps & {
    highlightOnFocus?: boolean;
    readOnly?: boolean;
  };

export type { Input2Size, Input2Tone, Input2Variant };

export const Input2 = React.forwardRef<HTMLInputElement, Input2Props>(
  (
    {
      className,
      type,
      highlightOnFocus,
      onFocus,
      tone,
      variant,
      size,
      readOnly,
      ...props
    },
    ref
  ) => {
    return (
      <input
        type={type}
        className={cn(
          input2ClassName({ tone, variant, size }),
          type === 'number' && hideNumberInputSpinnersClassName,
          className
        )}
        ref={ref}
        tabIndex={readOnly ? -1 : props.tabIndex}
        aria-readonly={readOnly ? 'true' : undefined}
        readOnly={readOnly}
        {...props}
        onFocus={e => {
          if (highlightOnFocus && !readOnly) {
            e.target.select();
          }
          onFocus?.(e);
        }}
      />
    );
  }
);
Input2.displayName = 'Input2';

export type TextArea2Props = Omit<
  React.ComponentProps<'textarea'>,
  'size' | 'readOnly'
> &
  Input2ChromeProps & {
    highlightOnFocus?: boolean;
    readOnly?: boolean;
    autoResize?: boolean;
  };

export const TextArea2 = React.forwardRef<HTMLTextAreaElement, TextArea2Props>(
  (
    {
      className,
      highlightOnFocus,
      onFocus,
      tone,
      variant,
      size,
      readOnly,
      autoResize = true,
      rows = 1,
      ...props
    },
    ref
  ) => {
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const adjustHeight = React.useCallback(() => {
      const textarea = textareaRef.current;
      if (!textarea || !autoResize) {
        return;
      }
      textarea.style.minHeight = 'auto';
      textarea.style.minHeight = `${textarea.scrollHeight}px`;
    }, [autoResize]);

    React.useEffect(() => {
      adjustHeight();
    }, [adjustHeight, props.value]);

    return (
      <textarea
        ref={mergeRefs(ref, textareaRef)}
        className={cn(
          textArea2ClassName({ tone, variant, size }),
          readOnly && 'resize-none',
          !autoResize && 'resize-none',
          className
        )}
        aria-readonly={readOnly ? 'true' : undefined}
        readOnly={readOnly}
        rows={rows}
        {...props}
        onFocus={e => {
          if (highlightOnFocus && !readOnly) {
            e.target.select();
          }
          onFocus?.(e);
        }}
        onChange={e => {
          adjustHeight();
          props.onChange?.(e);
        }}
      />
    );
  }
);
TextArea2.displayName = 'TextArea2';

export {
  input2ClassName,
  textArea2ClassName,
  input2ToneVariantShell,
  input2FieldChrome,
  input2Size
} from './_common/input2Chrome';
